import { Component, Emit, Inject, Prop, Vue, Watch } from 'vue-property-decorator'
import PdfView from '@/components/pdf-view/pdf-view.vue'
import {
    FilterByAnaReportEnum,
    filterTypes,
    initialReportsModalFormContentFilter,
    ReportModalFormContentFilter,
    ReportsModalFormContentFilterState
} from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/components/reports-modal-form-content-filter/reports-modal-form-content-filter.model'
import { ReportTypeEnum } from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/reports-modal.model'
import i18n from '@/lang/i18n'
import { batchReportServices } from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/services/batch-report.service'
import { EnumResponse } from '@/api/services/common.dto'
import { personnelService } from '@/api/resources/personnelService'
import { commonCenterService } from '@/api/services/common/center/common-center.service'
import { commonSimpleListEntity } from '@/api/resources/common/simple-list-entities/simple-list-entities.service'
import { SimpleListEntitiesAllowedEnum, SimpleListEntitiesResponseContent } from '@/api/resources/common/simple-list-entities/simple-list-entities.dto'
import { Validator } from 'vee-validate'
import { CommonCenterResponse } from '@/api/services/common/center/common-center.dto'
import { CommonPersonnelResponse } from '@/api/services/common/personnel/common-personnel.dto'
import { CommonResponsibilityCodeEnum } from '@/api/services/common/responsibility/common-responsibility.dto'

@Component({
    components: {
        PdfView
    }
})
export default class ReportsModalFormContentFilter extends Vue {
    @Inject() externalValidator!: Validator

    @Prop() readonly reportType!: ReportTypeEnum

    @Emit()
    stateChanged(): ReportsModalFormContentFilterState {
        return this.state
    }

    @Watch('state', { deep: true })
    onStateChange(): void {
        this.stateChanged()
    }

    state: ReportsModalFormContentFilterState
    constructor() {
        super()
        this.$validator = this.externalValidator
        this.state = initialReportsModalFormContentFilter()
    }

    deleteFilter(element:any): void {
        for (let i = 0; i < this.state.filters.length; i++) {
            if (this.state.filters[i] === element) {
                this.state.filters.splice(i, 1)
            }
        }
    }

    visiblePathologistsCombo(filter: ReportModalFormContentFilter): boolean {
        return filter.id === FilterByAnaReportEnum.PATHOLOGIST
    }

    visibleCentersCombo(filter: ReportModalFormContentFilter): boolean {
        return (filter.id === FilterByAnaReportEnum.REQUESTING_CENTER) || (filter.id === FilterByAnaReportEnum.DESTINATION_CENTER)
    }

    visibleDepartmentsCombo(filter: ReportModalFormContentFilter): boolean {
        return (filter.id === FilterByAnaReportEnum.REQUESTING_DEPARTMENT) || (filter.id === FilterByAnaReportEnum.DESTINATION_DEPARTMENT)
    }

    visiblePetitionersCombo(filter: ReportModalFormContentFilter): boolean {
        return filter.id === FilterByAnaReportEnum.REQUESTING_PROFESSIONAL
    }

    visibleDestinationPetitionersCombo(filter: ReportModalFormContentFilter): boolean {
        return filter.id === FilterByAnaReportEnum.DESTINATION_PROFESSIONAL
    }

    visibleRegimesCombo(filter: ReportModalFormContentFilter): boolean {
        return filter.id === FilterByAnaReportEnum.REGIME
    }

    visiblePrioritiesCombo(filter: ReportModalFormContentFilter): boolean {
        return filter.id === FilterByAnaReportEnum.PRIORITY
    }

    initializeState(): void {
        this.state = initialReportsModalFormContentFilter()
    }

    get addFilterButtonDisabled(): boolean {
        return this.state.filters.some(filter => filter.id === this.state.filterBy?.id) || !this.state.filterBy
    }

    get laboratoryId(): number {
        return this.$store.getters.labId
    }

    addFilter(): void {
        const filterToAdd: ReportModalFormContentFilter = {
            id: this.state.filterBy?.id!,
            filterType: filterTypes.SET,
            values: []
        }
        this.state.filters.push(filterToAdd)
    }

    async getCaseStates(): Promise<EnumResponse[]> {
        const response = await this.$_toCallSecure(batchReportServices.getAllCaseStatesForBatchReports(this.laboratoryId))
        return response ?? []
    }

    async getCenters(): Promise<CommonCenterResponse[]> {
        const response = await this.$_toCallSecure(commonCenterService.getActiveCenters())
        return response ?? []
    }

    async getDepartments(query: string): Promise<SimpleListEntitiesResponseContent[]> {
        const params = {
            active: true,
            size: 100,
            filter: query
        }
        const response = await this.$_toCallSecure(commonSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.DEPARTMENT, params))
        return response?.content ?? []
    }

    async getPetitioners(query: string): Promise<SimpleListEntitiesResponseContent[]> {
        const params = {
            active: true,
            size: 100,
            filter: query
        }
        const response = await this.$_toCallSecure(commonSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.PETITIONER, params))
        return response?.content ?? []
    }

    async getRegimes(query: string): Promise<SimpleListEntitiesResponseContent[]> {
        const params = {
            active: true,
            size: 100,
            filter: query
        }
        const response = await this.$_toCallSecure(commonSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.REGIME, params))
        return response?.content ?? []
    }

    async getPriorities(query: string): Promise<SimpleListEntitiesResponseContent[]> {
        const params = {
            active: true,
            size: 100,
            filter: query
        }
        const response = await this.$_toCallSecure(commonSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.PRIORITY, params))
        return response?.content ?? []
    }

    getPathologists(): Promise<CommonPersonnelResponse[]> {
        return personnelService.getPersonnelByLaboratoryAndResponsibility(this.laboratoryId, CommonResponsibilityCodeEnum.PATHOLOGIST)
    }

    getFilterLabel(filter: ReportModalFormContentFilter): string {
        switch (filter.id) {
        case FilterByAnaReportEnum.PATHOLOGIST:
            return i18n.tc('registry.modal.reports.filterBy.pathologist', 2)
        case FilterByAnaReportEnum.DESTINATION_PROFESSIONAL:
            return i18n.tc('registry.modal.reports.filterBy.destinationProfessional', 1)
        case FilterByAnaReportEnum.REQUESTING_PROFESSIONAL:
            return i18n.tc('registry.modal.reports.filterBy.requestingProfessional', 2)
        case FilterByAnaReportEnum.DESTINATION_CENTER:
            return i18n.tc('registry.modal.reports.filterBy.destinationCenter', 2)
        case FilterByAnaReportEnum.REQUESTING_CENTER:
            return i18n.tc('registry.modal.reports.filterBy.requestingCenter', 2)
        case FilterByAnaReportEnum.DESTINATION_DEPARTMENT:
            return i18n.tc('registry.modal.reports.filterBy.destinationDepartment', 2)
        case FilterByAnaReportEnum.REQUESTING_DEPARTMENT:
            return i18n.tc('registry.modal.reports.filterBy.requestingDepartment', 2)
        case FilterByAnaReportEnum.REGIME:
            return i18n.tc('registry.modal.reports.filterBy.regime', 2)
        case FilterByAnaReportEnum.PRIORITY:
            return i18n.tc('registry.modal.reports.filterBy.priority', 2)
        default:
            return ''
        }
    }

    get isPatientCasesReport(): boolean {
        return this.reportType === ReportTypeEnum.PATIENT_CASES
    }

    get isApaReports(): boolean {
        return this.reportType === ReportTypeEnum.APA_REPORT
    }
}
